'use client';

import { createContext, use, useEffect } from 'react';
import type { ReactNode } from 'react';

/**
 * One declaration, read by two rules that both need to know the same thing:
 * **is a page-covering modal overlay on screen?**
 *
 * A page-covering overlay is a `Dialog`, `Sheet`, `AlertDialog` or `BigSheet` —
 * something that dims the page and takes the whole viewport. A menu, select or
 * popover is NOT one: it floats over a few hundred pixels and the page behind
 * it stays the thing the reader is looking at.
 *
 * Radix cannot tell you the difference. Every modal Radix layer — the small ones
 * included — locks body scrolling through `react-remove-scroll`, which marks the
 * document with `data-scroll-locked`. So that attribute answers "some layer is
 * open", never "the page is covered", and anything keyed on it fires for a
 * kebab menu exactly as it does for a drawer.
 *
 * The two rules that need the narrower answer:
 *
 * 1. **The shell's scroll region is suppressed behind an overlay** (LF-5873,
 *    VALE-421). A drawer has its own scroll pane, so the shell's scrollbar
 *    beside it is redundant. Keyed on `data-scroll-locked` this also fired for
 *    every `⋯` menu: the region lost its scrollbar, the content box widened by
 *    the scrollbar's width, and every right-aligned control on the page jumped
 *    sideways and back (VALE-1418). `globals.css` now keys on
 *    {@link PAGE_OVERLAY_ATTRIBUTE} instead.
 *
 * 2. **A menu nested inside an overlay must be modal** (VALE-1138, VALE-1153,
 *    VALE-934). The overlay's `FocusScope` is trapped; a non-modal menu portals
 *    outside it and registers no scope of its own, so nothing pauses the trap,
 *    focus is pulled straight back out, and the menu closes in the tick it
 *    opened — a trigger that silently does nothing. `DropdownMenu` reads
 *    {@link useInsidePageOverlay} and forces modality rather than leaving each
 *    call site to remember which side of the line it is on.
 *
 * Both were previously answered per call site, and both regressed that way.
 */

/**
 * The z-index a page-covering overlay takes when it is opened from *inside*
 * another one.
 *
 * The base rung is `z-50`. `BigSheet` sits at `z-[60]`, so an overlay left at
 * the default is portalled to the body and then painted underneath the very
 * surface that opened it: the trigger works, focus moves, and nothing appears.
 * Stacking is by z-index here, not DOM order, so the nesting has to be answered
 * explicitly.
 *
 * **One rung, not a ladder, and that is deliberate.** {@link useInsidePageOverlay}
 * answers "inside an overlay?", never "how deep?", so every nested overlay lands
 * on this same rung and their relative order falls to DOM order — which Radix
 * makes correct for free, because it portals each layer to the end of `body` as
 * it opens, so the most recently opened paints on top. A per-depth ladder would
 * buy nothing and would need a depth counter the context does not carry.
 *
 * **Every page-covering overlay primitive must read this.** `Dialog` did and
 * `Sheet` did not, which is how a `Sheet` opened from inside a `Dialog` rendered
 * behind it: the "Add a new contact" panel opened from the hand-over dialog was
 * on screen, focusable and typeable, with the dialog painted over the top of it
 * (VALE-1802).
 */
export const NESTED_OVERLAY_Z = 'z-[70]';

/** Body attribute present while at least one page-covering overlay is mounted. */
export const PAGE_OVERLAY_ATTRIBUTE = 'data-page-overlay-open';

const PageOverlayContext = createContext(false);

/**
 * True when this subtree is rendered inside a page-covering modal overlay.
 *
 * Read it to make a decision automatic rather than a prop the call site has to
 * pass correctly; do not use it to branch on cosmetics.
 */
export function useInsidePageOverlay(): boolean {
  return use(PageOverlayContext);
}

/**
 * Marks its subtree as living inside a page-covering overlay, and marks the
 * document for as long as it is mounted.
 *
 * Render it inside the overlay's own `Content`, wrapping the content's
 * children — mounting and unmounting with the content is what makes the
 * document marker correct without anyone maintaining it. Overlays stack, so the
 * marker is ref-counted: it is removed when the last one closes, not the first.
 */
export function PageOverlayScope({ children }: { children: ReactNode }) {
  useEffect(() => {
    const { body } = document;
    const open = Number(body.getAttribute(PAGE_OVERLAY_ATTRIBUTE) ?? '0');
    body.setAttribute(PAGE_OVERLAY_ATTRIBUTE, String(open + 1));

    return () => {
      const remaining =
        Number(body.getAttribute(PAGE_OVERLAY_ATTRIBUTE) ?? '1') - 1;
      if (remaining > 0) {
        body.setAttribute(PAGE_OVERLAY_ATTRIBUTE, String(remaining));
      } else {
        body.removeAttribute(PAGE_OVERLAY_ATTRIBUTE);
      }
    };
  }, []);

  return <PageOverlayContext value={true}>{children}</PageOverlayContext>;
}

/**
 * Which modality a `DropdownMenu` gets, given where it is and what the call
 * site asked for.
 *
 * Inside an overlay the answer is always modal, whatever was requested — a
 * non-modal menu there cannot be opened at all, so honouring the request would
 * mean honouring it into a dead trigger. The LF-4072 body-lock hazard that
 * `modal={false}` exists to avoid is answered inside an overlay by *sequencing*
 * instead: close the drawer, then open the item's dialog on the next frame.
 *
 * On an ordinary page nothing is forced and Radix's own default (modal) stands
 * unless the call site says otherwise.
 */
export function resolveMenuModality(
  insidePageOverlay: boolean,
  requested: boolean | undefined
): boolean | undefined {
  return insidePageOverlay ? true : requested;
}
